import React, { useState } from 'react'
import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonPage,
  IonRow,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import { BaseSyntheticEvent } from 'react'
import { useHistory } from 'react-router'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useDispatch, useSelector } from 'react-redux'
import PatientList from '../PatientList/PatientList.component'
import DispatchList from '../dispatchList/DispatchList.component'
import './FullChart.page.css'
import timeLineImg from '../data/TimelineIcon.svg'
import trendMapImg from '../data/trend_map.svg'
import cadImportImg from '../data/CadImportIcon.svg'
import ScrollToTopButton from '../../../components/scrollToTopButton/ScrollToTopButton.component'
import { getScrollToTopSelector } from '../../../components/scrollToTopButton/selectors'
import { toggleScrollToTop } from '../../../components/scrollToTopButton/store/scrollToTop.actions'
import { useTranslation } from 'react-i18next'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { getSubmitChartSelector } from '../../submitChart/selectors'
import { isOnline } from '../../../utilities'
import { getConfigurationOptionsSelector } from '../../options/selectors'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'
import { upgradeStatus } from '../../home/store/home.types'
import { getUpgradeStatus } from '../../home/selectors/getUpgradeStatus.selector'
import { getAllPCRDocs } from '../../home/store/home.actions'
import { getUserSelector } from '../../user/selectors'
import Logger from '../../../services/logger.service'
import { resetCadRecords } from '../../options/store/options.actions'
import { getHomeSelector } from '../../home/selectors/getHome.selector'
import { clearLocalNotifications } from '../../../utilities/localNotifications.util'
import { saveRuleErrors } from '../../rules/store/rules.actions'
import { PULSARA_MODULE_ID } from '../../pulsara/store/pulsara.constants'

const FullChart: React.FC = () => {
  const { t } = useTranslation()

  const history = useHistory()
  const dispatch = useDispatch()

  const submitChart = useSelector(getSubmitChartSelector)
  const { success } = submitChart
  const [showToast, setShowToast] = useState(true)

  const configurationOptions = useSelector(getConfigurationOptionsSelector)
  const { serviceModules } = configurationOptions

  const scrollToTopButton = useSelector(getScrollToTopSelector)
  const { isVisible } = scrollToTopButton

  const upgradeStatusVal = useSelector(getUpgradeStatus)

  const user = useSelector(getUserSelector)
  const { serviceId, userId } = user
  const { hideComponent, isPCRError, pcrId } = useSelector(getHomeSelector)

  const hasCadModule = serviceModules.find((i: { module_id: number }) => i.module_id === 28)
  const hasPulsaraModule = serviceModules.find((i: { module_id: number }) => i.module_id === PULSARA_MODULE_ID)
  const showBothCadAndPulsaraSection = hasCadModule && hasPulsaraModule
  const showCadOnlySection = hasCadModule && !hasPulsaraModule
  const showPulsaraOnlySection = !hasCadModule && hasPulsaraModule

  const onBackClick = (e: BaseSyntheticEvent) => {
    Logger.info(`PCR: ${pcrId} closed by user: ${userId} in service: ${serviceId}`)

    e.preventDefault()
    dispatch(saveRuleErrors())
    dispatch(getAllPCRDocs())
    clearLocalNotifications()
    history.push(AppRouterConstants.HOME_ROUTE)
  }

  const onTimelineRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.TIMELINE_TAB)
  }

  const onCadImportRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(resetCadRecords())
    history.push(TabConstants.CAD_IMPORT)
  }

  const onPulsaraRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(AppRouterConstants.PULSARA_ROUTE)
  }

  const showNotImplementedAlert = () => {
    alert('Not implemented')
  }

  return (
    <IonPage id='FullChartPage'>
      <PCRHeader />
      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Close' onClick={onBackClick}>
              <IonIcon color='primary-alt' icon={chevronBackOutline} />
              <IonLabel color='primary-alt'>{t('common.buttons.Close')}</IonLabel>
            </IonButton>
          </IonButtons>
          <IonTitle>{t('fullChart.PatientCareReport')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      {success && (
        <IonToast
          isOpen={showToast}
          position='top'
          message={t('fullChart.chartSuccessfullySubmitted')}
          {...getToastProps(ToastColor.Success)}
          onDidDismiss={() => setShowToast(false)}
        />
      )}
      <IonContent
        color='light'
        fullscreen
        scrollEvents
        className='fullChart-content'
        onIonScroll={(e) => dispatch(toggleScrollToTop(e.detail.currentY))}>
        {upgradeStatusVal === upgradeStatus.IN_PROGRESS && <IonSpinner />}
        {upgradeStatusVal === upgradeStatus.FAILED && <h1>{t('fullChart.failedUpgradePCRMsg')}</h1>}
        {isPCRError === true && <h1>{t('fullChart.failedPCRInitialization')}</h1>}
        {!isPCRError && upgradeStatusVal === upgradeStatus.COMPLETE && (
          <>
            <IonList>
              <IonItem data-testid='Timeline' onClick={onTimelineRowClick}>
                <IonAvatar slot='start'>
                  <img src={timeLineImg} alt='' />
                </IonAvatar>
                <IonCol className='ion-padding pl-0'>
                  <IonRow>
                    <IonLabel>
                      <h2>{t('fullChart.details.Timeline')}</h2>
                    </IonLabel>
                  </IonRow>
                  <IonRow>
                    <IonLabel color='medium'>
                      <h3>{t('fullChart.details.VitalSignsAndInterventions')}</h3>
                    </IonLabel>
                  </IonRow>
                </IonCol>
              </IonItem>
              {hideComponent && (
                <IonItem onClick={showNotImplementedAlert}>
                  <IonAvatar slot='start'>
                    <img src={trendMapImg} alt='' />
                  </IonAvatar>
                  <IonCol className='ion-padding pl-0'>
                    <IonRow>
                      <IonLabel>
                        <h2>{t('fullChart.details.TrendMap')}</h2>
                      </IonLabel>
                    </IonRow>
                    <IonRow>
                      <IonLabel color='medium'>
                        <h3>{t('fullChart.details.PatientHealthAndTransportGraph')}</h3>
                      </IonLabel>
                    </IonRow>
                  </IonCol>
                </IonItem>
              )}
              {isOnline() && showBothCadAndPulsaraSection && (
                <>
                  <IonRow className='align-items-start ion-padding pt-0 pb-0'>
                    <IonCol className='data-import-option-avatar-col'>
                      <IonAvatar className='data-import-option-avatar'>
                        <img src={cadImportImg} alt='' />
                      </IonAvatar>
                    </IonCol>
                    <IonCol className='ion-padding pl-8 pr-0' size='10'>
                      <IonRow>
                        <IonLabel>
                          <h2>{t('fullChart.details.DataImport')}</h2>
                        </IonLabel>
                      </IonRow>
                      <IonRow className='ion-no-padding'>
                        <IonCol size='7' className='ion-no-padding'>
                          <IonItem className='data-import-option-item' lines='none' onClick={onCadImportRowClick}>
                            <IonLabel className='mr-1'>
                              <h2 className='data-import-option-title'>{t('fullChart.details.CadImport')}</h2>
                              <h3 className='data-import-option-description'>
                                {t('fullChart.details.DispatchPatientAndReceivingInfo')}
                              </h3>
                            </IonLabel>
                          </IonItem>
                        </IonCol>
                        <IonCol size='5' className='ion-no-padding'>
                          <IonItem className='data-import-option-item' lines='none' onClick={onPulsaraRowClick}>
                            <IonLabel>
                              <h2 className='data-import-option-title'>{t('fullChart.pulsara.Pulsara')}</h2>
                              <h3 className='data-import-option-description'>
                                {t('fullChart.pulsara.PulsaraDetails')}
                              </h3>
                            </IonLabel>
                          </IonItem>
                        </IonCol>
                      </IonRow>
                    </IonCol>
                  </IonRow>
                </>
              )}
              {isOnline() && (showCadOnlySection || showPulsaraOnlySection) && (
                <IonItem data-testid='CadImport' onClick={showCadOnlySection ? onCadImportRowClick : onPulsaraRowClick}>
                  <IonAvatar slot='start'>
                    <img src={cadImportImg} alt='' />
                  </IonAvatar>
                  <IonCol className='ion-padding pl-0'>
                    <IonRow>
                      <IonLabel>
                        <h2>
                          {showCadOnlySection ? t('fullChart.details.CadImport') : t('fullChart.pulsara.Pulsara')}
                        </h2>
                      </IonLabel>
                    </IonRow>
                    <IonRow>
                      <IonLabel color='medium'>
                        <h3>
                          {showCadOnlySection
                            ? t('fullChart.details.DispatchPatientAndReceivingInfo')
                            : t('fullChart.pulsara.PulsaraDetails')}
                        </h3>
                      </IonLabel>
                    </IonRow>
                  </IonCol>
                </IonItem>
              )}
            </IonList>
            <IonRow>
              <IonCol className='ion-align-self-start'>
                <IonItemDivider color='light' className='ion-padding'>
                  <IonLabel className='grid-item-divider-label' color='medium'>
                    {t('fullChart.Dispatch')}
                  </IonLabel>
                </IonItemDivider>
                <DispatchList />
              </IonCol>
              <IonCol offsetLg='0.2' offsetMd='0.2'>
                <IonItemDivider color='light' className='ion-padding'>
                  <IonLabel className='grid-item-divider-label' color='medium'>
                    {t('fullChart.Patient')}
                  </IonLabel>
                </IonItemDivider>
                <PatientList />
              </IonCol>
            </IonRow>
            <IonItemDivider color='light' />
          </>
        )}
        <IonItemDivider color='light' className='scroll-to-top' />
      </IonContent>

      {isVisible && <ScrollToTopButton isHorizontalEnd />}
    </IonPage>
  )
}

export default FullChart
